/**
 * Drift & Accumulation Tool Constants
 */

// Canonical REPS brand colors — exact hexes per Tim, 2026-06-09:
//   R Reliability = purple · E Efficiency = blue · P Productivity = red · S Safety = green
export const REPS_COLORS = {
  reliability: '#6D2D9D', // purple
  efficiency: '#0072C3',  // blue
  productivity: '#B70000', // red
  safety: '#00AF51',      // green
};

export const SEVERITY_COLORS = {
  healthy: '#2e7d32',
  monitor: '#f9a825',
  warning: '#c62828',
};

// OE / REPS warning bands. IMPORTANT: in this model HIGHER = WORSE (more entropy),
// so the LOWER the score the better (green), climbing through yellow → orange → red.
import { scoreBand } from './bands.js';

export const OE_BAND_COLORS = {
  healthy: '#2e7d32',  // green  — low entropy
  monitor: '#f9a825',  // yellow
  elevated: '#ef6c00', // orange
  critical: '#c62828', // red    — high entropy
};

/**
 * Map a 0–10 OE/REPS score to a warning band (higher = worse).
 * @param {number} score
 * @returns {{ key: string, label: string, color: string }}
 */
export function oeBand(score, threshold = 6, quadrant = null) {
  // P5-01: one threshold-aware band for the whole app (bands.js). The old 4.5 / 6 / 7.5 cut-offs
  // disagreed with the warning comparator; `elevated` and `critical` both map to the red band.
  const band = scoreBand(score, threshold, quadrant);
  return { key: band.key === 'warning' ? 'critical' : band.key, label: band.key === 'warning' ? 'Warning' : band.label, color: band.color, threshold: band.threshold };
}

export const DEFAULT_THRESHOLDS = {
  reliability: 6,
  efficiency: 6,
  productivity: 6,
  safety: 6,
};

export const SURVEY_STATUSES = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  CLOSED: 'closed',
};

export const SURVEY_SECTIONS = [
  {
    id: 'drift_perception',
    title: 'Section 1: Drift in Perception of Risk',
    instructions: 'Rate how much you agree with each statement on a scale of 1 (strongly disagree) to 10 (strongly agree).',
    notesKey: 'drift_perception_notes',
    notesLabel: 'Please share any examples of drift in risk perception you have observed.',
    questions: [
      { key: 'drift_perception_q1', text: 'I feel more comfortable with known risks than when they were first identified.' },
      { key: 'drift_perception_q2', text: 'Repeated exposure to hazards has reduced the sense of urgency around them.' },
      { key: 'drift_perception_q3', text: 'Serious risks are now viewed as "just part of the job."' },
      { key: 'drift_perception_q4', text: 'Declining risk awareness tends to go unchallenged by our team members.' },
      { key: 'drift_perception_q5', text: 'Leadership’s attention to known risks has decreased over time.' },
    ],
  },
  {
    id: 'drift_work',
    title: 'Section 2: Drift in How Work is Done',
    instructions: 'Rate how much you agree with each statement on a scale of 1 to 10.',
    notesKey: 'drift_work_notes',
    notesLabel: 'Please share any examples of drift in how work is done that you have observed.',
    questions: [
      { key: 'drift_work_q1', text: 'Shortcuts or workarounds that differ from established procedures have become routine.' },
      { key: 'drift_work_q2', text: '"How we actually do it" differs from standards, expectations, and/or associated policies, procedures, or job instructions.' },
      { key: 'drift_work_q3', text: 'Procedural deviations have come to feel "normal" over time.' },
      { key: 'drift_work_q4', text: 'Actual work practices would fail to match documented processes in an audit.' },
      { key: 'drift_work_q5', text: 'The Blue Line (actual practice) is diverging from the Black Line (intended practice).' },
    ],
  },
  {
    id: 'traps',
    title: 'Section 3: Accumulation \u2014 Traps',
    instructions: 'Rate the prevalence of each trap in your work environment on a scale of 1 (Non-existent) to 10 (Epidemic).',
    scaleLabels: { min: 'Non-existent', max: 'Epidemic' },
    notesKey: 'traps_notes',
    notesLabel: 'Which traps are most significant in your work environment? Please provide examples.',
    questions: [
      { key: 'traps_time_pressure', text: 'Time Pressure' },
      { key: 'traps_peer_pressure', text: 'Peer Pressure' },
      { key: 'traps_distractions', text: 'Distractions' },
      { key: 'traps_change_off_normal', text: 'Change/Off-Normal Conditions' },
      { key: 'traps_multiple_tasks', text: 'Multiple / Simultaneous / Competing Tasks' },
      { key: 'traps_physical_environment', text: 'Physical Environment' },
      { key: 'traps_overconfidence', text: 'Overconfidence' },
      { key: 'traps_fatigue', text: 'Fatigue' },
      { key: 'traps_vague_guidance', text: 'Vague, Conflicting, or Interpretive Guidance' },
      { key: 'traps_first_last_shift', text: 'Lack of focus due to First Shift/Last Shift' },
      { key: 'traps_mental_stress', text: 'Mental Stress' },
    ],
  },
  {
    id: 'roadblocks',
    title: 'Section 4: Accumulation \u2014 Roadblocks',
    instructions: 'Rate how much these barriers affect your work on a scale of 1 to 10.',
    notesKey: 'roadblocks_notes',
    notesLabel: 'Please share any examples of roadblocks you have encountered.',
    questions: [
      { key: 'roadblocks_q1', text: 'Unnecessary barriers make doing the correct work harder or slow time to completion.' },
      { key: 'roadblocks_q2', text: 'Administrative requirements slow work without adding value.' },
      { key: 'roadblocks_q3', text: 'Resources are difficult to access when needed.' },
      { key: 'roadblocks_q4_communication', text: 'Communication breakdowns between shifts, teams, or departments impede safe, correct, and/or efficient task execution.' },
    ],
  },
  {
    id: 'landmines',
    title: 'Section 5: Accumulation \u2014 Landmines',
    instructions: 'Rate how much these hidden risks exist in your environment on a scale of 1 to 10.',
    notesKey: 'landmines_notes',
    notesLabel: 'Please share any examples of hidden risks or landmines you are aware of.',
    questions: [
      { key: 'landmines_q1', text: 'There are hidden weaknesses/setups in policies, procedures/job instructions, systems, and/or in how work flows are structured.' },
      { key: 'landmines_q2', text: 'Outdated or conflicting procedures persist without being identified or corrected.' },
      { key: 'landmines_q3', text: 'Known systemic issues are not formally addressed.' },
      { key: 'landmines_q4_labeling_checklists', text: 'Labeling, signage, or checklists in our work environment contain inaccuracies or gaps.' },
    ],
  },
];

/** Number of rated items in the standard D&A instrument (derived — never hard-code it in UI). */
export const STANDARD_QUESTION_COUNT = SURVEY_SECTIONS.reduce((n, s) => n + s.questions.length, 0);

/**
 * OE Element Names — the 5 diagnostic elements measured by the D&A tool
 */
export const OE_ELEMENTS = [
  { key: 'driftPerception', label: 'Drift in Perception of Risk', sectionId: 'drift_perception' },
  { key: 'driftWork', label: 'Drift in How Work is Done', sectionId: 'drift_work' },
  { key: 'traps', label: 'Accumulation: Traps', sectionId: 'traps' },
  { key: 'roadblocks', label: 'Accumulation: Roadblocks', sectionId: 'roadblocks' },
  { key: 'landmines', label: 'Accumulation: Landmines', sectionId: 'landmines' },
];

/**
 * Trap Names — the 11 human performance traps measured in Section 3
 */
export const TRAP_NAMES = [
  'Time Pressure',
  'Peer Pressure',
  'Distractions',
  'Change/Off-Normal Conditions',
  'Multiple / Simultaneous / Competing Tasks',
  'Physical Environment',
  'Overconfidence',
  'Fatigue',
  'Vague, Conflicting, or Interpretive Guidance',
  'Lack of focus due to First Shift/Last Shift',
  'Mental Stress',
];

// USER_ROLES removed (unused duplicate) — the canonical role set lives in
// src/services/roleService.js ROLE_DEFINITIONS / roleLabel().
